import { useEffect, useMemo, useState } from 'react';

import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { formatDate } from '@bitstopco/bitstop-theme/helpers';
import { skipToken } from '@reduxjs/toolkit/query';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import {
  clearName,
  fmtMoment,
  isNewClear,
  itemTime,
  money,
  originalTxCount,
  originalWorth,
  simulateItems,
  simulatedTxCount,
  simulatedWorth,
  txCount,
} from '@/helpers/cashLogs';

import {
  useCreateCashLogMutation,
  useSimulateCreateClearQuery,
} from '@/store/api/services/machines/cashlogs';
import { useGetMachinesQuery } from '@/store/api/services/machines/machines';

import type { BoxClearSimulatedItemView } from '@/types';

interface MachineOption {
  id: number;
  label: string;
  address?: string;
}

const todayUtcDate = () => new Date().toISOString().slice(0, 10);
const nowUtcTime = () => new Date().toISOString().slice(11, 16);

/** Combine the UTC date + time fields into an ISO-8601 instant for the API. */
const toIsoUtc = (date: string, time: string) => {
  const parsed = new Date(`${date}T${time}:00Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

/**
 * Display label for a simulate item: falls back for the open/pending bucket,
 * which the simulate endpoint returns with no `box_clear_id`.
 */
const clearLabel = (item?: BoxClearSimulatedItemView) =>
  item?.box_clear_id != null ? `Clear ${item.box_clear_id}` : 'Open period';

/**
 * Create Manual Clear modal.
 *
 * Renders a 3-row placement preview computed live by the boro simulate
 * endpoint (`POST /v2/CashBox/simulate`): never client-side as the
 * operator edits machine / date / time, then commits via boro
 * (`POST /v2/CashBox`). Reason is required; bills are derived from the
 * reassigned transactions, so the form has no breakdown fields.
 *
 * Transaction counts (DEV-1862) follow the DEV-1611 copy: the new clear shows
 * "~N tx absorbed from clear #X" and the next clear "M tx remaining". They are
 * hidden when the simulate payload doesn't carry counts.
 */
const CreateManualClear = ({ hide, props }: ModalChildrenProps) => {
  const presetAtmId = (props as { atmId?: number })?.atmId;

  const [machine, setMachine] = useState<MachineOption | null>(
    presetAtmId ? { id: presetAtmId, label: `Machine ${presetAtmId}` } : null,
  );
  const [machineSearch, setMachineSearch] = useState('');
  const [date, setDate] = useState(todayUtcDate());
  const [time, setTime] = useState(nowUtcTime());
  const [reason, setReason] = useState('');

  // --- Machine combobox reuses the shared ATM listing endpoint ---
  const { data: machinesResponse, isFetching: machinesLoading } = useGetMachinesQuery({
    search_text: machineSearch,
    pagination: { items_per_page: 20, current_page: 1, sort_by: 'name', sort_ascending: true },
  });

  const machineOptions = useMemo<MachineOption[]>(
    () =>
      (machinesResponse?.data ?? []).map((m) => ({
        id: m.id,
        label: m.name ? `${m.name} (#${m.id})` : `Machine #${m.id}`,
        address: m.location?.full_address,
      })),
    [machinesResponse],
  );

  // --- Placement preview debounced simulate call ---
  const createdAtUtc = useMemo(() => toIsoUtc(date, time), [date, time]);
  const [simulateArgs, setSimulateArgs] = useState<{ atm_id: number; created_at: string }>();

  useEffect(() => {
    if (!machine || !createdAtUtc) {
      setSimulateArgs(undefined);
      return;
    }
    const t = setTimeout(
      () => setSimulateArgs({ atm_id: machine.id, created_at: createdAtUtc }),
      350,
    );
    return () => clearTimeout(t);
  }, [machine, createdAtUtc]);

  const {
    data: simulation,
    isFetching: loadingPreview,
    isError: previewError,
  } = useSimulateCreateClearQuery(simulateArgs ?? skipToken);

  // Split the simulate window into previous / new / next around the new
  // boundary. The clear that loses transactions to the new boundary ("next")
  // is the non-new item whose simulated worth differs from its original worth
  // it can be the open/pending bucket, which carries no id or timestamp.
  // "previous" is the newest unaffected clear at or before the new timestamp.
  const placement = useMemo(() => {
    const items = simulateItems(simulation);
    const newItem = items.find(isNewClear);
    const others = items.filter((i) => !isNewClear(i));
    if (others.length === 0) return { noClears: true as const };
    const next = others.find((i) => simulatedWorth(i) !== originalWorth(i));
    const newTime = newItem ? itemTime(newItem) : 0;
    const previous = [...others]
      .filter((i) => i !== next && itemTime(i) <= newTime)
      .sort((a, b) => itemTime(b) - itemTime(a))[0];
    return { noClears: false as const, newItem, previous, next };
  }, [simulation]);

  const [createCashLog, { isLoading: submitting }] = useCreateCashLogMutation();

  const reasonValid = reason.trim().length > 0;
  const canSubmit = !!machine && !!createdAtUtc && reasonValid && !loadingPreview;

  const handleSubmit = async () => {
    if (!machine || !createdAtUtc) return;
    try {
      // `POST /v2/CashBox` returns 201 with no body, so there is no new clear id
      // to navigate to close the modal; the list refetches via cache tags.
      await createCashLog({
        atm_id: machine.id,
        created_at: createdAtUtc,
        reason: reason.trim(),
      }).unwrap();
      toast.success('Manual clear created');
      hide();
    } catch {
      toast.error('Could not create the manual clear. Please try again.');
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Add a new boundary between two existing clears. Transactions between the previous clear
          and this timestamp will be reassigned.
        </Typography>

        <Stack gap={2}>
          {/* Machine combobox */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Machine
            </Typography>
            <Autocomplete<MachineOption>
              options={machineOptions}
              value={machine}
              loading={machinesLoading}
              onChange={(_, v) => setMachine(v)}
              onInputChange={(_, v) => setMachineSearch(v)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search by name or ID…"
                  data-test="cash-log-create-machine"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {machinesLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(p, option) => (
                <li {...p} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.label}
                    </Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">
                        {option.address}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
            />
          </Box>

          {/* Date / time */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pulled on (UTC)
            </Typography>
            <Stack direction="row" gap={1.5}>
              <TextField
                type="date"
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
              />
              <TextField
                type="time"
                size="small"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                fullWidth
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Enter a reason for this clear to see how it affects placement before submitting.
              Adjusting the time may change which clear the new clear pulls from. Please, review the
              placement preview below for details.
            </Typography>
          </Box>

          {/* Reason */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Reason
            </Typography>
            <TextareaAutosize
              minRows={4}
              maxRows={8}
              placeholder="Provide a reason for creating this manual clear..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-test="cash-log-create-reason"
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '12px 14px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                backgroundColor: 'transparent',
                outline: 'none',
                cursor: 'text',
              }}
            />
          </Box>

          {/* Placement preview */}
          {!machine ? (
            <Alert severity="info">Select a machine to preview placement.</Alert>
          ) : (
            <Box
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
              data-test="cash-log-create-preview"
            >
              <Box
                sx={{
                  px: 1.75,
                  py: 1.25,
                  bgcolor: 'grey.50',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  Placement preview
                </Typography>
              </Box>

              {loadingPreview ? (
                <Stack p={1.5} gap={1}>
                  <Skeleton variant="rounded" height={50} />
                  <Skeleton variant="rounded" height={50} />
                  <Skeleton variant="rounded" height={50} />
                </Stack>
              ) : previewError ? (
                <Box sx={{ px: 1.75, py: 1.25 }}>
                  <Typography variant="caption" color="error.main">
                    Could not load the placement preview. Adjust the machine or timestamp and try
                    again.
                  </Typography>
                </Box>
              ) : placement.noClears ? (
                <Box sx={{ px: 1.75, py: 1.25, bgcolor: 'warning.light', color: 'warning.dark' }}>
                  <Typography variant="caption">
                    No existing clears found for this machine. New clear will stand alone.
                  </Typography>
                </Box>
              ) : (
                <>
                  <PlacementTable
                    previous={placement.previous}
                    newItem={placement.newItem}
                    next={placement.next}
                  />
                  <Box sx={{ px: 1.75, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      {placementSummary(placement.newItem, placement.next)}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-test="cash-log-create-submit"
        >
          Create clear
        </LoadingButton>
      </DialogActions>
    </>
  );
};

/**
 * One plain sentence on what moves: transactions before the new time leave the
 * next clear for the new one; later ones stay put.
 */
const placementSummary = (
  newItem?: BoxClearSimulatedItemView,
  next?: BoxClearSimulatedItemView,
) => {
  if (!newItem?.created_at || !next) {
    return 'New clear will stand alone. No adjacent clear to absorb from.';
  }
  return `Transactions made before ${fmtMoment(newItem.created_at)} UTC move from ${clearName(next)} into the new clear. Later ones stay in ${clearName(next)}.`;
};

/** One amount cell: worth on top, tx count underneath. */
const AmountCell = ({
  worth,
  worthColor = 'text.primary',
  strike = false,
  tx,
  txColor = 'text.secondary',
}: {
  worth?: string;
  worthColor?: string;
  strike?: boolean;
  tx?: string;
  txColor?: string;
}) => (
  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>
    <Typography
      variant="body2"
      fontWeight={600}
      color={worth ? worthColor : 'text.disabled'}
      sx={strike ? { textDecoration: 'line-through' } : undefined}
    >
      {worth ?? '—'}
    </Typography>
    {tx && (
      <Typography variant="caption" color={txColor} sx={{ display: 'block' }}>
        {tx}
      </Typography>
    )}
  </TableCell>
);

/** Formats a count into caption text, or undefined when the count is unknown. */
const txText = (count: number | undefined, suffix = '') =>
  count != null ? `${txCount(count)}${suffix}` : undefined;

/** UTC date label for a simulate item; em-dash when there's no timestamp. */
const fmtPlacementDate = (iso?: string) =>
  iso ? formatDate(iso, 'MMM d, yyyy', { timeZone: 'UTC' }) : '—';

interface PlacementTableProps {
  previous?: BoxClearSimulatedItemView;
  newItem?: BoxClearSimulatedItemView;
  next?: BoxClearSimulatedItemView;
}

/**
 * Now/After table for a create-clear simulation: the previous boundary
 * (context, unchanged), the new clear, and the clear it pulls transactions
 * from.
 */
const PlacementTable = ({ previous, newItem, next }: PlacementTableProps) => {
  const headSx = {
    py: 0.75,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'text.secondary',
  } as const;
  const numHeadSx = { ...headSx, textAlign: 'right' } as const;
  const nextLost = !!next && simulatedWorth(next) < originalWorth(next);

  return (
    <Table
      size="small"
      sx={{
        '& td, & th': { px: 1.75 },
        '& tbody tr:last-of-type td': { borderBottom: 0 },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={headSx}>Clear</TableCell>
          <TableCell sx={headSx}>Pulled on (UTC)</TableCell>
          <TableCell sx={numHeadSx}>Now</TableCell>
          <TableCell sx={numHeadSx}>After</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {previous ? (
          <TableRow>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2">{clearLabel(previous)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Previous boundary
              </Typography>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2" color="text.secondary">
                {fmtPlacementDate(previous.created_at)}
              </Typography>
            </TableCell>
            <AmountCell worth={money(originalWorth(previous))} worthColor="text.secondary" />
            <AmountCell worth={money(simulatedWorth(previous))} worthColor="text.secondary" />
          </TableRow>
        ) : (
          newItem && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="caption" color="text.disabled">
                  No prior clear. The new clear will be the oldest boundary.
                </Typography>
              </TableCell>
            </TableRow>
          )
        )}

        {newItem && (
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography variant="body2" fontWeight={600}>
                  New clear
                </Typography>
                <Chip
                  label="New"
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Being created manually
              </Typography>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2">{fmtPlacementDate(newItem.created_at)}</Typography>
            </TableCell>
            <AmountCell />
            <AmountCell
              worth={`~${money(simulatedWorth(newItem))}`}
              worthColor="primary.main"
              tx={
                next
                  ? txText(simulatedTxCount(newItem), ` absorbed from ${clearName(next)}`)
                  : 'No adjacent clear to absorb from'
              }
              txColor="primary.main"
            />
          </TableRow>
        )}

        {next && (
          <TableRow>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2">{clearLabel(next)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Gives its earlier transactions to the new clear
              </Typography>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>
              <Typography variant="body2" color="text.secondary">
                {fmtPlacementDate(next.created_at)}
              </Typography>
            </TableCell>
            <AmountCell
              worth={money(originalWorth(next))}
              strike
              tx={txText(originalTxCount(next))}
            />
            <AmountCell
              worth={money(simulatedWorth(next))}
              worthColor={nextLost ? 'error.main' : 'text.primary'}
              tx={txText(simulatedTxCount(next), ' remaining')}
              txColor={nextLost ? 'error.main' : 'text.secondary'}
            />
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default CreateManualClear;
